import React, { useState } from 'react';
import copy from 'copy-to-clipboard';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';
import { API_ENDPOINT } from 'constants/payloads/lessons/invite';
import { getCoachFollowUrl, getPlayerFollowUrl } from 'constants/user';
import { CoachStatusEnum } from 'types/generated/client';
import { trackEvent } from 'services/client/analytics';
import { ONBOARDING_CLIENTS_ADDED } from 'services/client/analytics/events';
import { useApiGateway } from 'hooks/useApi';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import AttachIcon from 'svg/AttachIcon';
import CloseIcon from 'svg/CloseIcon';
import Minus from 'svg/Minus';
import PlusPlain from 'svg/PlusPlain';
import { InvitePlayersModalProps } from 'screens/CoachLoggedInScreen/props';
import { Button, ButtonText } from 'components/Button';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface FormData {
  players: { fullName: string; email: string }[];
}

const InvitePlayersModal: React.FC<InvitePlayersModalProps> = ({
  isOpen,
  handleClose,
  onSuccess,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [step, setStep] = useState<'input' | 'qr'>('input');
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useGetCurrentUser();
  const { post: invitePlayer } = useApiGateway(API_ENDPOINT);

  const profileUrl =
    typeof window !== 'undefined' && user
      ? user.coachStatus === CoachStatusEnum.Active
        ? getCoachFollowUrl(user?.username || user?.id || '')
        : getPlayerFollowUrl(user?.username || user?.id || '')
      : '';

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      players: [{ fullName: '', email: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'players',
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFileName(file.name);
    setCsvFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());

        if (lines.length === 0) {
          throw new Error('CSV file is empty');
        }

        // Check if first line contains @ to determine if it has headers
        const hasHeaders = !lines[0].includes('@');
        const dataLines = hasHeaders ? lines.slice(1) : lines;

        const newPlayers = dataLines.map((line, index) => {
          const [fullName, email] = line.split(',').map((field) => field.trim());

          if (!email?.includes('@')) {
            throw new Error('Invalid email format - emails must contain @');
          }

          return {
            fullName: fullName || '',
            email: email || '',
          };
        });

        if (newPlayers.length === 0) {
          throw new Error('No valid data found in CSV');
        }

        // Reset form with new players
        reset({
          players: newPlayers,
        });

        toast.success(`Successfully loaded ${newPlayers.length} players`);
        trackEvent(ONBOARDING_CLIENTS_ADDED, {
          count: newPlayers.length,
          method: 'csv_upload',
          fileName: file.name,
          fileSize: file.size,
        });
      } catch (err: unknown) {
        if (err instanceof Error) {
          toast.error(err.message);
        } else {
          toast.error('Error processing CSV file');
        }
        reset({
          players: [{ fullName: '', email: '' }],
        });
      }
    };

    reader.onerror = () => {
      toast.error('Error reading file');
      reset({
        players: [{ fullName: '', email: '' }],
      });
    };

    reader.readAsText(file);
  };

  const resetFile = () => {
    setFileName('');
    setCsvFile(null);
    reset({
      players: [{ fullName: '', email: '' }],
    });
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);

      // Filter out empty entries
      const validPlayers = data.players.filter(
        (player) => player.fullName.trim() && player.email.trim(),
      );

      if (validPlayers.length === 0) {
        toast.error('Please add at least one player with valid information');
        return;
      }

      trackEvent(ONBOARDING_CLIENTS_ADDED, {
        count: validPlayers.length,
        method: fileName ? 'csv_upload' : 'manual',
      });

      const promises = validPlayers.map((player) =>
        invitePlayer({
          payload: {
            email: player.email,
            fullName: `${player.fullName}`.trim(),
          },
        }),
      );

      await Promise.allSettled(promises);

      toast.success(
        `Successfully invited ${validPlayers.length} player${validPlayers.length > 1 ? 's' : ''}`,
      );

      // Reset form and close modal
      reset({
        players: [{ fullName: '', email: '' }],
      });
      setFileName('');
      setCsvFile(null);
      setStep('input');

      // Call success callback if provided
      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      console.error('Error inviting players:', error);
      toast.error('Failed to invite players. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    handleClose();
    setTimeout(() => {
      setCsvFile(null);
      setFileName('');
      setStep('input');
      setCsvFile(null);
      reset({
        players: [{ fullName: '', email: '' }],
      });
    }, 300);
  };

  const handleNoEmail = () => {
    setStep('qr');
  };

  const handleBack = () => {
    setStep('input');
  };

  const handleCopyLink = () => {
    copy(profileUrl);
    toast.success('Share profile link copied');
    if (navigator.share) {
      navigator.share({
        title: `${user?.fullName} - book lessons on Bounce`,
        text: `More details at ${profileUrl}`,
        url: profileUrl,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleCloseModal} classNameMaxWidth="sm:max-w-2xl">
      <div className="flex h-full flex-col">
        {/* Modal Header */}
        <div
          className={classNames(
            'flex flex-row items-center justify-between border-color-border-card-lightmode px-10 py-5 dark:border-color-border-card-darkmode max-sm:px-ds-lg sm:border-b',
            step === 'input' ? 'border-b-0' : '',
          )}
        >
          <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
            Invite your players
          </h2>
          <ButtonText onClick={handleCloseModal}>
            <CloseIcon className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-3.5 max-sm:w-3.5" />
          </ButtonText>
        </div>

        {/* Modal Content */}
        <div
          className={classNames(
            'flex flex-1 flex-col items-center px-10 max-sm:px-ds-lg',
            step === 'input' ? 'pt-4' : 'pt-6',
          )}
        >
          {step === 'input' ? (
            <div className="flex w-full flex-col gap-8">
              {/* CSV Upload Section */}
              <div className="relative rounded-xl border-2 border-dashed border-color-border-input-lightmode p-6 dark:border-color-border-input-darkmode">
                <div className="flex flex-col items-center gap-3">
                  <h3 className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    Upload CSV File
                  </h3>
                  <p className="typography-product-body text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Your CSV file should have these exact column headers:
                  </p>
                  <p className="typography-product-body text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Column A: "Full Name"
                    <br />
                    Column B: "Email"
                  </p>
                  <p className="typography-product-body text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Example row: John Smith, john@email.com
                  </p>
                  {fileName ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                        {fileName}
                      </span>
                      <ButtonText type="button" onClick={resetFile}>
                        <div className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M4 4L12 12M4 12L12 4"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </ButtonText>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer items-center gap-1 text-color-brand-highlight">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <PlusPlain className="h-4 w-4" />
                      <span className="typography-product-button-label-medium text-color-brand-highlight">
                        Choose CSV File
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Manual Entry Section */}
              <div className="flex flex-col gap-3">
                <h3 className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Enter manually
                </h3>

                {fields.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <InputField
                          name={`players.${index}.fullName`}
                          register={register}
                          errors={errors}
                          placeholder="Enter Full Name"
                          inputType={InputType.Regular}
                        />
                      </div>
                      {fields.length > 1 && (
                        <ButtonText type="button" onClick={() => remove(index)}>
                          <Minus className="h-5 w-5" />
                        </ButtonText>
                      )}
                    </div>
                    <div className="flex-1">
                      <InputField
                        name={`players.${index}.email`}
                        register={register}
                        errors={errors}
                        placeholder="Enter email"
                        inputType={InputType.Regular}
                      />
                    </div>
                  </div>
                ))}

                <ButtonText
                  type="button"
                  onClick={() => append({ fullName: '', email: '' })}
                  className="flex items-center gap-1 text-color-brand-highlight"
                >
                  <PlusPlain className="h-4 w-4" />
                  Add Another Player
                </ButtonText>
              </div>
            </div>
          ) : (
            /* QR Code Screen */
            <div className="flex w-full flex-col items-center gap-12">
              <div className="flex flex-col items-center gap-6">
                <p className="typography-product-body-highlight max-w-[30.125rem] text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Share your profile to add players to your Clients list.
                  <br />
                  Players should press "Follow" button.
                </p>

                {/* QR Code */}
                <div className="flex h-[220px] w-[220px] items-center justify-center rounded-lg bg-white p-4">
                  <QRCode value={profileUrl} size={200} />
                </div>

                {/* Copy Link Button */}
                <Button
                  onClick={handleCopyLink}
                  variant="brand"
                  size="lg"
                  iconLeft={<AttachIcon className="h-5 w-5" />}
                  className="max-w-fit"
                >
                  Copy link to your listing
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          className={classNames(
            'mt-8 flex  items-center justify-end gap-4 border-color-border-card-lightmode px-6 py-5 dark:border-color-border-card-darkmode sm:border-t',
            step === 'input' && 'max-sm:flex-wrap',
          )}
        >
          {step === 'input' ? (
            <>
              <Button
                onClick={handleNoEmail}
                variant="secondary"
                size="lg"
                className="flex-1 whitespace-nowrap"
              >
                I don't have email
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                variant="primary"
                size="lg"
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Inviting...' : 'Invite'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleBack} variant="secondary" size="lg" className="flex-1">
                Back
              </Button>
              <Button onClick={handleCloseModal} variant="primary" size="lg" className="flex-1">
                Done
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InvitePlayersModal;
